import { useState } from 'react'
import { Eye, EyeOff, Trash2, KeyRound, CheckCircle2 } from 'lucide-react'
import { useAppStore } from '../../stores/app'
import { useOrgApiKeys, useSetOrgApiKey, useDeleteOrgApiKey } from '../../hooks/useAdminConfig'
import { LoadingSpinner } from '../ui/LoadingSpinner'

const PROVIDERS: { id: string; label: string; placeholder: string }[] = [
  { id: 'openai', label: 'OpenAI', placeholder: 'sk-...' },
  { id: 'anthropic', label: 'Anthropic', placeholder: 'sk-ant-...' },
  { id: 'google', label: 'Google / Gemini', placeholder: 'AIza...' },
  { id: 'perplexity', label: 'Perplexity', placeholder: 'pplx-...' },
  { id: 'tavily', label: 'Tavily', placeholder: 'tvly-...' },
]

interface InlineKeyInputProps {
  provider: string
  placeholder: string
  onSave: (key: string) => void
  onCancel: () => void
  isPending: boolean
}

function InlineKeyInput({ provider: _provider, placeholder, onSave, onCancel, isPending }: InlineKeyInputProps) {
  const [value, setValue] = useState('')
  const [show, setShow] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = value.trim()
    if (!trimmed) return
    onSave(trimmed)
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 mt-2">
      <div className="relative flex-1 max-w-sm">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="input pr-10 font-mono text-sm"
          autoFocus
          disabled={isPending}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <button
        type="submit"
        className="btn btn-primary text-sm"
        disabled={isPending || !value.trim()}
      >
        {isPending ? 'Guardando...' : 'Guardar'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="btn btn-secondary text-sm"
        disabled={isPending}
      >
        Cancelar
      </button>
    </form>
  )
}

export function SettingsAPIKeysSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data: keys, isLoading, error } = useOrgApiKeys(organizationId ?? undefined)
  const setKey = useSetOrgApiKey()
  const deleteKey = useDeleteOrgApiKey()

  const [editing, setEditing] = useState<string | null>(null)

  const configuredMap = new Map(
    (keys ?? []).map((k) => [k.provider, k])
  )

  const handleSave = (provider: string, key: string) => {
    setKey.mutate(
      { provider, key },
      {
        onSuccess: () => setEditing(null),
      }
    )
  }

  const handleDelete = (provider: string) => {
    deleteKey.mutate({ provider })
  }

  if (!organizationId) return null

  return (
    <div className="card">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-slate-500" />
          API Keys de proveedores
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Configura claves propias por proveedor. Si no se configura, se usa la clave del sistema.
          Los valores se almacenan cifrados.
        </p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500">No se pudieron cargar las API keys.</p>
      )}

      {!isLoading && !error && (
        <div className="divide-y divide-slate-100">
          {PROVIDERS.map((prov) => {
            const configured = configuredMap.get(prov.id)
            const isEditing = editing === prov.id
            const isSaving = setKey.isPending && setKey.variables?.provider === prov.id
            const isDeleting = deleteKey.isPending && deleteKey.variables?.provider === prov.id

            return (
              <div key={prov.id} className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-700 w-36">{prov.label}</span>
                    {configured ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                        <CheckCircle2 className="w-3 h-3" />
                        {configured.key_preview}
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-medium text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5">
                        Usando clave del sistema
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {!isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary text-xs"
                        onClick={() => setEditing(prov.id)}
                        disabled={isSaving || isDeleting}
                      >
                        {configured ? 'Actualizar' : 'Configurar'}
                      </button>
                    )}
                    {configured && !isEditing && (
                      <button
                        type="button"
                        className="btn text-xs text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200"
                        onClick={() => handleDelete(prov.id)}
                        disabled={isDeleting}
                        title="Eliminar clave"
                      >
                        {isDeleting ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <InlineKeyInput
                    provider={prov.id}
                    placeholder={prov.placeholder}
                    onSave={(key) => handleSave(prov.id, key)}
                    onCancel={() => setEditing(null)}
                    isPending={isSaving}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
